<template>
  <v-container class="h-100 pa-4" fluid>
    <v-row>
      <v-col cols="12" md="4">
        <div class="text-h5 text-center mb-8 font-weight-medium">Order Summary</div>
        <v-sheet class="border-opacity-25 overflow-hidden border-thin rounded-lg pa-4">
          <v-row>
            <v-col cols="6">
              <div>Registration Level:</div>
            </v-col>
            <v-col cols="6" class="text-right">
              {{ product.teamLevel?.team_level }}
            </v-col>
          </v-row>
          <v-row class="justify-space-between">
            <v-col cols="6">
              <div>Total:</div>
            </v-col>
            <v-col cols="6" class="text-right">
              <div>${{ (amount / 100).toFixed(2) }}</div>
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>
      <v-col cols="12" md="8">
        <v-form ref="billingForm">
          <v-row>
            <v-col cols="12" md="12">
              <v-text-field
                v-model="billingFormData.name"
                label="Billing Name"
                autocomplete="name"
                :error-messages="errors.name"
                :rules="[rules.required]"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <CountrySelect
                v-model="billingFormData.country"
                label="Billing Country"
                autocomplete="country-name"
                :rules="[rules.required]"
                :countryName="true"></CountrySelect>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="billingFormData.street_address"
                label="Billing Street Address"
                autocomplete="street-address"
                :error-messages="errors.street_address"
                :rules="[rules.required]"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="billingFormData.city"
                label="Billing City"
                autocomplete="address-level2"
                :rules="[rules.required]"
                :error-messages="errors.city"></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <RegionSelect
                v-model="billingFormData.state"
                label="Billing State"
                autocomplete="address-level1"
                :rules="[rules.required]"
                :country="billingFormData.country"
                :countryName="true">
              </RegionSelect>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="billingFormData.postal_code"
                label="Billing Postal Code"
                autocomplete="postal-code"
                :rules="[rules.postalCode]"
                :error-messages="errors.postal_code"></v-text-field>
            </v-col>
          </v-row>
        </v-form>
        <v-divider></v-divider>
        <StripeElements
          v-if="stripeLoaded"
          ref="elementsComponent"
          :stripe-key="stripeKey"
          :instance-options="stripeOptions"
          :elements-options="elementsOptions"
          class="pb-4">
          <StripeElement ref="paymentComponent" type="payment" :options="paymentElementOptions" />
        </StripeElements>
        <v-divider></v-divider>
        <form @submit.prevent="handleSubmit">
          <v-btn color="primary" block class="rounded-lg mb-8" elevation="2" type="submit"
            >Pay Now</v-btn
          >
        </form>
      </v-col>
    </v-row>
  </v-container>
  <v-overlay :model-value="overlay" class="align-center justify-center">
    <v-progress-circular color="primary" size="64" indeterminate></v-progress-circular>
  </v-overlay>
</template>

<script setup lang="ts">
import { loadStripe } from '@stripe/stripe-js';
import type { StripeElementsOptionsMode, StripePaymentElementOptions } from '@stripe/stripe-js';

import { onBeforeMount, onMounted, ref, toRef, useTemplateRef, watch } from 'vue';
import { StripeElement, StripeElements } from 'vue-stripe-js';
import { createPaymentIntent, getProductByVariantId } from '@/controllers';
import CountrySelect from '@/components/ui/forms/countryregion/country-select.vue';
import RegionSelect from '@/components/ui/forms/countryregion/region-select.vue';
import { useOrderStore, useRegistrationStore } from '@/stores';
import type { VForm } from 'vuetify/components';
import { IProductDTO, ProductDTO } from '@/dtos';
import { rules } from '@/helpers';
interface Props {
  refresh: boolean;
}
const props = defineProps<Props>();

const refresh = toRef(props, 'refresh', false);
const orderStore = useOrderStore();
const emit = defineEmits(['registrationFull']);
const registrationStore = useRegistrationStore();
const getMyRegistration = registrationStore.getMyRegistration.data;
const overlay = ref<boolean>(false);

interface BillingForm {
  name: string;
  country: string;
  street_address: string;
  city: string;
  state: string;
  postal_code: string;
}
const billingForm = ref<VForm | null>(null);
const billingFormData = ref<BillingForm>({
  name: '',
  country: '',
  street_address: '',
  city: '',
  state: '',
  postal_code: '',
});
const errors = ref<BillingForm>({
  name: '',
  country: '',
  street_address: '',
  city: '',
  state: '',
  postal_code: '',
});

const stripeKey = import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY;
const stripeOptions = ref({
  // https://stripe.com/docs/js/initializing#init_stripe_js-options
});

const elementsOptions = ref<StripeElementsOptionsMode>();
const paymentElementOptions = ref<StripePaymentElementOptions>({
  paymentMethodOrder: ['card'],
});

const stripeLoaded = ref(false);
const clientSecret = ref('');
const product = ref<IProductDTO>(new ProductDTO());

const elementsComponent = useTemplateRef('elementsComponent');
const paymentComponent = useTemplateRef('paymentComponent');

const amount = ref<number>(0);

const stripeElements = async () => {
  elementsOptions.value = {
    // https://stripe.com/docs/js/elements_object/create#stripe_elements-options
    mode: 'payment',
    amount: amount.value,
    currency: 'usd',
    appearance: {
      theme: 'flat',
    },
  };
};

// This is here to make sure if they hit previous and change their registration type that we update the cart.
watch(
  () => refresh.value,
  async (value: boolean) => {
    if (value) {
      await refreshAmount(false);
      await getPaymentIntent();
    }
  },
);

async function getPaymentIntent() {
  const response = await createPaymentIntent();
  clientSecret.value = response.clientSecret;
}

const handleSubmit = async () => {
  const stripeInstance = elementsComponent.value?.instance;
  const elements = elementsComponent.value?.elements;
  let billFormValidation = false;
  let stripValid = false;
  if (billingForm.value) {
    billFormValidation = (await billingForm.value.validate()).valid;
  }

  if (elements) {
    const data = await elements.submit();
    if (!data.error) {
      stripValid = true;
    }
  }

  if (await registrationStore.checkIfBranchIsFull()) {
    emit('registrationFull', true);
    return;
  }

  if (billFormValidation && stripeInstance && stripValid) {
    overlay.value = true;
    let formOrder = getMyRegistration?.order;

    if (!formOrder) {
      throw 'Order should not be empty';
    }
    formOrder.billing_name = billingFormData.value.name;
    formOrder.billing_city = billingFormData.value.city;
    formOrder.billing_country = billingFormData.value.country;
    formOrder.billing_postal_code = billingFormData.value.postal_code;
    formOrder.billing_state = billingFormData.value.state;
    formOrder.billing_street_address = billingFormData.value.street_address;

    orderStore.save.mutateAsync(formOrder);

    const returnUrl = `${window.location.origin}/order/${getMyRegistration?.order?.orderId}/completed`;
    const { error } = await stripeInstance.confirmPayment({
      elements,
      clientSecret: clientSecret.value,
      confirmParams: {
        return_url: returnUrl,
      },
    });

    if (error) {
      overlay.value = false;
    }
  }
};

const refreshAmount = async (firstTime: boolean) => {
  if (getMyRegistration && getMyRegistration.order && getMyRegistration.order.productVariant) {
    product.value = await getProductByVariantId(getMyRegistration.order.productVariant.id);
    amount.value = getMyRegistration.order?.amount * 100;
  }
  if (firstTime) {
    await stripeElements();
  }
};
onBeforeMount(async () => {
  await refreshAmount(true);
  loadStripe(stripeKey).then(() => {
    stripeLoaded.value = true;
  });
});

onMounted(async () => {
  await getPaymentIntent();
});
</script>
